import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { socketEventEmitter } from "../../../../socket";
import { useNotification } from "../../../../context/NotificationContext";
import PageService from "../../../../services/PageService";
import UiTestingAccordion from "./UiTestingAccordion";
import TestCasesAccordion from "./TestCasesAccordion";
import GenerateTestSettingsModal from "../../../modals/GenerateTestSettingsModal";

const OverviewTab = ({
  pageData,
  isScanLoading,
  handleScanPage,
  discoveryPageToggler,
  setDiscoveryPageToggler,
  isGenerateTestsInProgress,
}) => {
  const navigate = useNavigate();
  const [allTestCases, setAllTestCases] = useState([]);
  const [testCasesTotal, setTestCasesTotal] = useState(0);
  const [isGeneratingLocal, setIsGeneratingLocal] = useState(false);
  const [generateTestSettingsModalOpen, setGenerateTestSettingsModalOpen] =
    useState(false);
  const showNotification = useNotification();

  useEffect(() => {
    const getAllTestCases = async () => {
      try {
        const testCasesData = await PageService.getAllTestCasesByPageId(
          pageData.page.id,
        );
        setAllTestCases(testCasesData.testCases);
        setTestCasesTotal(testCasesData.total);
      } catch (error) {
        console.error("Error fetching test cases:", error);
      }
    };

    if (pageData?.page.id) {
      getAllTestCases();
    }

    const handleTaskCompleted = async (data) => {
      if (data.pageId === pageData?.page?.id) {
        setDiscoveryPageToggler(!discoveryPageToggler);
        setIsGeneratingLocal(false);
      }
    };

    const handleTaskFailed = async (data) => {
      if (data.pageId === pageData?.page?.id) {
        setDiscoveryPageToggler(!discoveryPageToggler);
        setIsGeneratingLocal(false);
      }
    };

    socketEventEmitter.on("taskCompleted", handleTaskCompleted);
    socketEventEmitter.on("taskFailed", handleTaskFailed);

    return () => {
      socketEventEmitter.off("taskCompleted", handleTaskCompleted);
      socketEventEmitter.off("taskFailed", handleTaskFailed);
    };
    // eslint-disable-next-line
  }, [pageData?.page?.id, discoveryPageToggler]);

  const handleGenerateTestCases = () => {
    setGenerateTestSettingsModalOpen(true);
  };

  const handleGoToTestJourneys = () => {
    navigate("/test-journeys");
  };

  const handleRunGenerateTest = async (context) => {
    try {
      setIsGeneratingLocal(true);
      await PageService.discoverTestCasesByPageId(pageData.page.id, context);
      showNotification("Test case generation started", "success");
    } catch (error) {
      console.error("Error starting test case generation:", error);
      showNotification("Failed to start test case generation", "error");
      setIsGeneratingLocal(false);
    }
  };

  const effectiveGenerating = isGenerateTestsInProgress || isGeneratingLocal;

  const renderDecisionMakingScheme = () => (
    <Box className="decisionScheme">
      <Typography className="decisionSchemeTitle">
        How would you explain this feature to a new teammate?
      </Typography>
      <Box className="decisionSchemeCards">
        <Box className="decisionSchemeCard">
          <AdsClickOutlinedIcon className="decisionSchemeIcon" />
          <Typography className="decisionSchemeQuote">
            &quot;Just open this page.&quot;
          </Typography>
          <LoadingButton
            className="decisionSchemeButton"
            variant="contained"
            color="secondary"
            onClick={handleGenerateTestCases}
            loading={effectiveGenerating}
            disabled={effectiveGenerating || isScanLoading}
            loadingIndicator="Generating…"
            sx={{ opacity: effectiveGenerating ? 0.8 : 1 }}
          >
            Generate Test Cases
          </LoadingButton>
        </Box>
        <Box className="decisionSchemeCard">
          <RouteOutlinedIcon className="decisionSchemeIcon" />
          <Typography className="decisionSchemeQuote">
            &quot;Let me show you...&quot;
          </Typography>
          <Button
            className="decisionSchemeButton"
            variant="contained"
            color="secondary"
            onClick={handleGoToTestJourneys}
          >
            Go to Test Journeys
          </Button>
        </Box>
        <Box className="decisionSchemeCard">
          <VisibilityOutlinedIcon className="decisionSchemeIcon" />
          <Typography className="decisionSchemeQuote">
            &quot;Just do a visual pass. Don’t test logic.&quot;
          </Typography>
          <LoadingButton
            className="decisionSchemeButton"
            variant="contained"
            color="secondary"
            onClick={handleScanPage}
            loading={isScanLoading}
            disabled={isScanLoading || effectiveGenerating}
            loadingIndicator="Scanning…"
            sx={{ opacity: isScanLoading ? 0.8 : 1 }}
          >
            UI Scan
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box className="overviewTabContentContainer">
      {pageData &&
      (pageData.page.passed_ui_test_count ||
        pageData.page.failed_ui_test_count ||
        testCasesTotal) ? (
        <>
          {pageData.page.summary ? (
            <Typography className="overviewTabContentSummary">
              {pageData.page.summary}
            </Typography>
          ) : null}
          {pageData.page.passed_ui_test_count ||
          pageData.page.failed_ui_test_count ? (
            <UiTestingAccordion pageData={pageData} />
          ) : (
            <LoadingButton
              className="testsActionButton"
              variant="contained"
              color="secondary"
              onClick={handleScanPage}
              loading={isScanLoading}
              disabled={isScanLoading || isGenerateTestsInProgress}
              loadingIndicator="Scanning…"
              sx={{ opacity: isScanLoading ? 0.8 : 1 }}
            >
              START UI SCAN
            </LoadingButton>
          )}
          {allTestCases.length ? (
            <TestCasesAccordion
              testCasesData={allTestCases}
              testCasesTotal={testCasesTotal}
              isGenerateTestsInProgress={isGenerateTestsInProgress}
              isScanLoading={isScanLoading}
            />
          ) : (
            renderDecisionMakingScheme()
          )}
        </>
      ) : (
        <Box className="overviewTabEmptyContentContainer">
          {renderDecisionMakingScheme()}
        </Box>
      )}
      <GenerateTestSettingsModal
        open={generateTestSettingsModalOpen}
        setOpen={setGenerateTestSettingsModalOpen}
        onRun={handleRunGenerateTest}
      />
    </Box>
  );
};

export default OverviewTab;
